/**
 * Ortak Soru Stüdyosu — Responsive Düzen Kabuğu
 *
 * Düzen kuralları (USE_QUESTION_STUDIO_LAYOUT açıkken):
 *   ≥1536px : Sol (öğrenci) | Orta (editör) | Sağ (medya tepsisi)  — 3 sütun
 *   1280–1535px : Sol (öğrenci) | Orta (editör) — 2 sütun; Sağ altta tam genişlik
 *   <1280px : Tek sütun; sol→orta→sağ alt alta (RS-03)
 *             (Mevcut kural NOT: hiçbir panel gizlenmez)
 *
 * KURAL: Bu bileşen layout-only'dir; iş mantığı veya API çağrısı içermez.
 *        Body'de yatay kaydırma OLMAMALI (overflow-x: hidden zorunlu).
 *
 * Export 1 (default): QuestionStudioShell — 3 panel prop alan saf layout.
 * Export 2:           QuestionStudioShellWrapper — isStudio=false'ta children'ı doğrudan döndürür.
 */

import { useEffect, useState } from "react";

// ─── Viewport genişliği hook (resize dinleyicisi, tek kayıt) ──────────────────
function useWindowWidth() {
  const [w, setW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1920
  );
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler, { passive: true });
    return () => window.removeEventListener("resize", handler);
  }, []);
  return w;
}

// ─── Ana Shell ────────────────────────────────────────────────────────────────
/**
 * Responsive üç-panel düzeni.
 *
 * Props:
 *   leftPanel   — sol panel JSX (öğrenci görünümü)
 *   rightPanel  — sağ panel JSX (medya tepsisi)
 *   children    — orta panel JSX (soru editörü)
 */
export default function QuestionStudioShell({ leftPanel, rightPanel, children }) {
  const w = useWindowWidth();

  // ── ≥1536px: Üç sütun ───────────────────────────────────────────────────
  if (w >= 1536) {
    return (
      <div
        className="flex w-full overflow-x-hidden overflow-hidden"
        style={{ height: "calc(100vh - 53px)" }}
        data-studio-layout="3col"
      >
        {/* Sol: Öğrenci Görünümü */}
        <aside
          data-studio-panel="left"
          className="shrink-0 overflow-y-auto border-r border-slate-200"
          style={{ width: "28%" }}
        >
          {leftPanel}
        </aside>

        {/* Orta: Editör */}
        <div
          data-studio-panel="middle"
          className="flex-1 min-w-0 overflow-y-auto"
        >
          {children}
        </div>

        {/* Sağ: Media V2 Tepsisi */}
        <aside
          data-studio-panel="right"
          className="shrink-0 overflow-y-auto border-l border-slate-200"
          style={{ width: "30%" }}
        >
          {rightPanel}
        </aside>
      </div>
    );
  }

  // ── 1280–1535px: İki sütun + Alt Tepsi ───────────────────────────────────
  if (w >= 1280) {
    return (
      <div
        className="flex flex-col w-full overflow-x-hidden overflow-hidden"
        style={{ height: "calc(100vh - 53px)" }}
        data-studio-layout="2col"
      >
        {/* Üst satır: Sol + Orta */}
        <div className="flex flex-1 min-h-0">
          <aside
            data-studio-panel="left"
            className="shrink-0 overflow-y-auto border-r border-slate-200"
            style={{ width: "38%" }}
          >
            {leftPanel}
          </aside>
          <div
            data-studio-panel="middle"
            className="flex-1 min-w-0 overflow-y-auto"
          >
            {children}
          </div>
        </div>

        {/* Alt: Media V2 tepsisi — tam genişlik */}
        <div
          data-studio-panel="right-bottom"
          className="w-full border-t border-slate-200 overflow-y-auto shrink-0"
          style={{ maxHeight: "42vh" }}
        >
          {rightPanel}
        </div>
      </div>
    );
  }

  // ── <1280px: Tek sütun — sol, orta, sağ alt alta (RS-03) ───────────────────
  // Not: Hiçbir panel gizlenmez; kullanıcı kaydırarak tümüne erişebilir.
  return (
    <div data-studio-layout="1col" className="w-full flex flex-col">
      {/* Sol: Öğrenci Görünümü */}
      <div data-studio-panel="left" className="w-full border-b border-slate-200">
        {leftPanel}
      </div>
      {/* Orta: Editör */}
      <div data-studio-panel="middle" className="w-full">
        {children}
      </div>
      {/* Sağ: Medya Tepsisi */}
      <div data-studio-panel="right-mobile" className="w-full border-t border-slate-200">
        {rightPanel}
      </div>
    </div>
  );
}

// ─── Conditional Wrapper ──────────────────────────────────────────────────────
/**
 * isStudio=false  → children doğrudan döndürülür (mevcut davranış korunur).
 * isStudio=true   → QuestionStudioShell ile üç-panel düzenine sarılır.
 *
 * Props:
 *   isStudio    — bool
 *   leftPanel   — sol panel JSX
 *   rightPanel  — sağ panel JSX
 *   children    — orta panel JSX (form content)
 */
export function QuestionStudioShellWrapper({ isStudio, leftPanel, rightPanel, children }) {
  if (!isStudio) return <>{children}</>;
  return (
    <QuestionStudioShell leftPanel={leftPanel} rightPanel={rightPanel}>
      {children}
    </QuestionStudioShell>
  );
}
